/**
 * Die EINE Regel „Quelle + Stand" an jeder Recherche-Angabe.
 *
 * Bauplan: docs/KONZEPT-RECHERCHE-SICHTBARKEIT.md § 2.2 (jede Angabe traegt Quelle + Stand),
 * § 4.1 (`looked_up_at` = wann tatsaechlich extern gefragt wurde), Befund B6 (die Daten liegen
 * bereits, sie werden nur nicht gerendert). Kriterium R1.6 in docs/LOOP-RECHERCHE.md.
 *
 * ── Warum diese drei Zeilen ein eigenes Paket sind ────────────────────────────────────────────
 * Weil es genau EINE Zeile geben muss. Vor R1.6 stand die Herkunftszeile ausschliesslich in
 * `@maestro/firmenprofil` (R0.9) — dort privat, fuer die Karte „Wer fragt hier an?". Das
 * Kunden-Profil, die Places-Karte und die Wetter-Karte zeigten dieselbe Art von Angabe ganz ohne
 * Stand. Haette jede Flaeche sich ihre Zeile selbst gebaut, gaebe es vier Formulierungen und vier
 * Meinungen darueber, was passiert, wenn das Datum fehlt — und genau dort entsteht das
 * Falschdatum: irgendwo faellt jemand auf `created_at` zurueck, weil das „auch ein Datum" ist.
 *
 * `apps/web` hat KEINEN Test-Runner (in R0.2 festgehalten, in R0.7/R0.9 erneut). Eine Regel wie
 * „ohne `looked_up_at` steht dort NICHT irgendein Datum" ist in einer React-Komponente unpruefbar.
 * Sie wohnt deshalb hier; die Tests liegen in `apps/api/test/recherche-stand.test.ts`, weil genau
 * diese Suite garantiert in der CI laeuft. Dieses Paket hat darum bewusst KEIN `test`-Skript
 * (`apps/api/test/ci-testabdeckung.test.ts` verlangt fuer jedes Paket MIT `test`-Skript einen
 * eigenen CI-Aufruf — ein zusaetzliches Ziel ohne zusaetzliche Sicherheit).
 *
 * ── Was hier NICHT hingehoert ────────────────────────────────────────────────────────────────
 * Zustaende („ausgesetzt", „veraltet") — die bildet `@maestro/anreicherung` ab (R0.7) und prueft
 * ihre Vollstaendigkeit gegen eine abgenommene Liste. Feldauswahl je Karte — die entscheidet
 * `@maestro/firmenprofil` (R0.9). Hier steht nur: aus Quelle + Zeitstempel wird eine Zeile.
 *
 * ── Reinheit ─────────────────────────────────────────────────────────────────────────────────
 * Kein `new Date()` ohne Argument, keine implizite Zone: `zeitzone` ist Parameter. Fehlt sie,
 * formatiert `Intl` in der Zone der Laufzeit — im Browser also in der des Betrachters (Vorgabe aus
 * R0.7, dort gegen Berlin/Kolkata/UTC gemessen), im Test in der uebergebenen Zone.
 */

/* ────────────────────────────── Quellen ────────────────────────────────────────────────────── */

/**
 * Die Quellen, aus denen MAESTRO heute Angaben ueber einen Kunden bezieht. Eine Recherche-Angabe
 * ohne benannte Quelle gibt es nicht — „irgendwoher" ist keine Herkunft.
 *
 * Bewusst als Liste und nicht als freier String: eine neue Quelle bekommt hier einen Namen und
 * wird damit ueberall gleich geschrieben. „Google Places" und „Google-Eintrag" stehen beide drin
 * und meinen NICHT dasselbe: „Google Places" ist der bezahlte Einzelabruf zu einem KUNDEN
 * (Kunden-Profil, § 4.7), „Google-Eintrag" der einmalig bestaetigte EIGENE Betriebseintrag, den
 * der Tageslauf abgleicht (R0.12, § 4.6).
 */
export const RECHERCHE_QUELLEN = [
  "Anfrage",
  /**
   * R3.4: der vom Betreiber gepflegte Kundendatensatz. Keine Fremdquelle — und trotzdem eine
   * Quelle mit Namen und Stand: sobald seine Anschrift neben der einer Fremdquelle steht, muss
   * dabeistehen, woher sie kommt und wann sie zuletzt angefasst wurde (`customers.updated_at`).
   * Ohne diesen Namen hieße die eigene Angabe „irgendwoher", und § 3.4 stellt gerade sie zuerst
   * („Was wir selbst erlebt haben").
   */
  "Kundendatensatz",
  "Handelsregister",
  "Bundesanzeiger",
  /**
   * R1.7: eine AUSKUNFTEI (Creditreform, boniforce, handelsregister.ai …). Der Name steht hier,
   * damit ein spaeter EINGEKAUFTER Bonitaetswert dieselbe Herkunftszeile traegt wie jede andere
   * Angabe. MAESTRO erzeugt selbst keinen — § 31 BDSG verlangt dafuer ein anerkanntes
   * mathematisch-statistisches Verfahren. Wo frueher „KI-Zusammenfassung" stand (bis R1.7 die
   * Quelle der selbst formulierten Bonitaetsprosa), steht jetzt bewusst nichts: diese Quelle gibt
   * es nicht mehr, und ein Name ohne Quelle waere eine Einladung, sie neu zu bauen.
   */
  "Auskunftei",
  "Google Places",
  "Google-Eintrag",
  /**
   * R4.4: der LEI-Datensatz aus dem GLEIF-Register (Open Data, CC0). Steht hier, damit auch ein
   * Kreuzcheck-Befund seine Herkunftszeile trägt — er ist eine Angabe wie jede andere, nur mit
   * der zusätzlichen Auflage, nie allein zu stehen (`@maestro/quellenabgleich`).
   */
  "GLEIF",
  "Bright Sky (DWD)",
  // R3.1: die Pflichtangaben nach § 5 DDG von der Website der Kundenfirma. Der Name ist bewusst
  // schlicht „Impressum" und nicht „Website" — er benennt das Dokument, aus dem die Angabe
  // stammt, nicht den Ort, an dem es lag.
  "Impressum",
  /**
   * R3.2: die Bestaetigungsabfrage beim Bundeszentralamt fuer Steuern (§ 18e UStG). Der Name
   * benennt bewusst das VERFAHREN und nicht die Behoerde: was hier ausgewiesen wird, ist nicht
   * „was das BZSt ueber die Firma weiss", sondern „dass am Tag X bestaetigt wurde". Der Stand
   * dieser Zeile ist der Zeitpunkt UNSERES Abrufs; der Zeitpunkt, fuer den die Aussage gilt,
   * steht getrennt daneben (`bestaetigt_am`, sql/238) — siehe `@maestro/ustid-anzeige`.
   */
  "USt-IdNr.-Bestätigung",
] as const;
export type RechercheQuelle = (typeof RECHERCHE_QUELLEN)[number];

/**
 * Was an die Stelle des Datums tritt, wenn die Quelle keins geliefert hat.
 *
 * AUSDRUECKLICH benannt statt weggelassen: „Handelsregister" allein saehe aus wie eine gepruefte
 * Angabe von heute. Und ausdruecklich KEIN Rueckfall auf `created_at` (Anlage der Zeile) oder
 * `now()` — beides waere ein plausibel aussehendes Falschdatum, also eine Behauptung. Diese
 * Entscheidung hat R0.9 fuer die Anfragen-Karte getroffen; R1.6 macht sie zur Regel fuer alle
 * Flaechen.
 */
export const OHNE_DATUM = "ohne Datum";

/* ────────────────────────────── Formatierung ───────────────────────────────────────────────── */

/** Wie genau der Stand angezeigt wird. */
export type StandGenauigkeit =
  /** „12.06.2026" — fuer gespeicherte Ergebnisse, die Tage bis Monate gelten. */
  | "tag"
  /**
   * „06.08.2026, 14:05" — fuer Quellen, die bei jedem Aufruf frisch geholt werden und innerhalb
   * eines Tages veralten (Wetter). Ein reines Tagesdatum waere dort formal wahr und praktisch
   * ohne Aussage: es stuende immer „heute".
   */
  | "minute";

/**
 * „12.06.2026" bzw. „12.06.2026, 14:05" in der Zone des Betrachters.
 * `null`, wenn der Wert fehlt ODER unlesbar ist — ein unlesbarer Zeitstempel ist kein Datum.
 */
export function standDatum(
  iso: string | null | undefined,
  zeitzone?: string,
  genauigkeit: StandGenauigkeit = "tag",
): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return null;
  const datum = d.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: zeitzone,
  });
  if (genauigkeit === "tag") return datum;
  const uhrzeit = d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", timeZone: zeitzone });
  return `${datum}, ${uhrzeit}`;
}

/**
 * Die fertige Herkunftszeile, die unter (oder neben) einer Recherche-Angabe steht:
 * „Handelsregister · Stand 12.06.2026" bzw. „Handelsregister · ohne Datum".
 *
 * Bewusst hier zusammengesetzt und nicht in der Komponente: „ohne Datum" ist eine Aussage, keine
 * Formatierung — sie muss gepruefte Regel sein. Die Ratsche in
 * `apps/api/test/recherche-stand.test.ts` haelt fest, dass niemand in `apps/web` eine eigene baut.
 */
export function standZeile(
  quelle: RechercheQuelle,
  standIso: string | null | undefined,
  zeitzone?: string,
  genauigkeit: StandGenauigkeit = "tag",
): string {
  const stand = standDatum(standIso, zeitzone, genauigkeit);
  return stand ? `${quelle} · Stand ${stand}` : `${quelle} · ${OHNE_DATUM}`;
}

/* ────────────────────────────── Die Ratsche kennt sich selbst ──────────────────────────────── */

/**
 * Woran die Ratsche erkennt, dass eine Flaeche einen Stand ausgibt.
 *
 * Diese Liste steht hier und nicht im Test, weil sie zur REGEL gehoert: wer eine neue
 * Anzeige-Regel baut, sieht hier, welchen Ausgabenamen sie tragen muss, damit die Ratsche sie als
 * „traegt einen Stand" erkennt. Sie zaehlt AUSGABEN (drei Namen), nicht Felder oder Flaechen — die
 * Prueflisten der Ratsche werden aus dem Quelltext abgeleitet, nicht gepflegt.
 *
 *  - `standZeile`      — dieser Baustein, direkt aufgerufen (Kunden-Profil, Places, Wetter).
 *  - `herkunft`        — das Ausgabefeld von `@maestro/firmenprofil` (R0.9), das `standZeile` fuellt.
 *  - `quelleUndStand`  — das Ausgabefeld von `@maestro/oeffnungszeiten-anzeige` (R0.12).
 */
export const STAND_AUSGABEN = ["standZeile", "herkunft", "quelleUndStand"] as const;
